import React from 'react';
import {
  Card,
  CardActions,
  CardContent,
  Grid,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  IDealerCTA,
  Dealer,
  IDealerLocatorLabels,
} from 'interfaces/dealerLocator';
import { Icon, Icons } from 'components/Global/Icon/Icon';
import { appPalette } from 'theme/App.palette';
import { useDispatch, useSelector } from 'react-redux';
import {
  preferredDealer,
  setPreferredDealerId,
  removePreferredDealerId,
  setSelectedDealer,
} from 'redux/slices/dealerSlice';
import {
  LinkButton,
  LinkButtonType,
} from 'components/Global/LinkButton/LinkButton';
import TextRender from 'components/Global/TextRender/TextRender';
import { useStyles } from './DealerCard.styles';
import { trackEvent } from 'helpers/Analytics';
import { updateUrlParameter } from 'helpers/Url';
import { useHistory } from 'react-router-dom';

function getCtaURL(button: IDealerCTA, dealer: Dealer): string {
  let result = button.url;
  const dealerKeys = Object.keys(dealer);
  dealerKeys.forEach((dealerKey: string) => {
    //@ts-ignore
    result = result.split(`[${dealerKey}]`).join(dealer[dealerKey]);
  });
  return result;
}
export interface IDealerCard {
  dealer: Dealer;
  index?: number;
  useDealerCtas: boolean;
  handleDetailsClick: Function;
  isDealerDetail?: boolean;
  labels: IDealerLocatorLabels;
  CTAs: { [key: string]: IDealerCTA };
  isInModal?: boolean;
}

export function DealerCard({
  dealer,
  index,
  useDealerCtas,
  handleDetailsClick,
  isDealerDetail,
  labels,
  CTAs,
  isInModal,
}: IDealerCard): JSX.Element {
  const history = useHistory();
  const classes = useStyles();
  const theme = useTheme();
  const isHondaThemed = theme.themeName === 'honda';
  const { acura_red, light_grey_2, iconMainColor } = appPalette.colors;
  const currentPreferredDealer = useSelector(preferredDealer);
  const dispatch = useDispatch(); // redux dispatch
  const matchesMediumBreakpoint = useMediaQuery(
    theme.breakpoints.up('viewportMedium'),
    { noSsr: true }
  );

  const selectDealer = () => {
    matchesMediumBreakpoint && !isInModal && window.scrollTo({ top: 0 });
    if (dealer && dealer.dealerNumber) {
      updateUrlParameter('dealer', dealer.dealerNumber?.toString(), history);
    } else {
      updateUrlParameter('dealer', 0, history);
    }
    dispatch(setSelectedDealer(dealer));
  };

  const getButtonType = (type: string) => {
    switch (type) {
      case 'button':
        return LinkButtonType.redOutlined;

      case 'link':
        return LinkButtonType.text;
      default:
        return LinkButtonType.text;
    }
  };

  const handlePreferredDealerId = () => {
    let actionLabel = `${
      isDealerDetail ? 'dealer details' : 'results'
    }:deselect preferred dealer`;
    if (dealer.dealerNumber === currentPreferredDealer) {
      dispatch(removePreferredDealerId());
    } else {
      actionLabel = `${
        isDealerDetail ? 'dealer details' : 'results'
      }:select preferred dealer`;
      dispatch(setPreferredDealerId(dealer.dealerNumber as string));
    }
    trackEvent({
      label: actionLabel,
      category: {
        includePageName: !isDealerDetail,
        text: isDealerDetail ? 'locate dealer details' : 'results',
      },
      type: 'selector',
      dealer: {
        dealerId: dealer.dealerNumber,
        dealerZipcode: dealer.zipCode,
        dealerName: dealer.name,
        dealerAddress: `${dealer.address as string}, ${dealer.city}, ${
          dealer.state
        } ${dealer.zipCode}`,
      },
    });
  };

  const handleDealerDirectionClick = () => {
    trackEvent({
      label: `${isDealerDetail ? 'dealer details' : 'results'}:${
        dealer.name
      } directions`,
      category: {
        includePageName: !isDealerDetail,
        text: isDealerDetail ? 'locate dealer details' : 'results',
      },
      type: 'link',
      dealer: {
        dealerId: dealer.dealerNumber,
        dealerZipcode: dealer.zipCode,
        dealerName: dealer.name,
        dealerAddress: `${dealer.address as string}, ${dealer.city}, ${
          dealer.state
        } ${dealer.zipCode}`,
      },
    });
  };

  const handleDealerPhoneClick = () => {
    trackEvent({
      label: `${isDealerDetail ? 'dealer details' : 'results'}:call ${
        dealer.name
      }`,
      category: {
        includePageName: !isDealerDetail,
        text: isDealerDetail ? 'locate dealer details' : 'results',
      },
      type: 'link',
      dealer: {
        dealerId: dealer.dealerNumber,
        dealerZipcode: dealer.zipCode,
        dealerName: dealer.name,
        dealerAddress: `${dealer.address as string}, ${dealer.city}, ${
          dealer.state
        } ${dealer.zipCode}`,
        dealerPhone: dealer.phone,
      },
    });
  };

  const getTrackEventData = (clickText: string, ctaType?: LinkButtonType) => {
    return {
      label: `${isDealerDetail ? 'dealer details' : 'results'}:${clickText}`,
      category: {
        includePageName: !isDealerDetail,
        text: isDealerDetail ? 'locate dealer details' : 'results',
      },
      type: ctaType === LinkButtonType.text ? 'link' : 'cta',
      dealer: {
        dealerId: dealer.dealerNumber,
        dealerZipcode: dealer.zipCode,
        dealerName: dealer.name,
        dealerAddress: `${dealer.address as string}, ${dealer.city}, ${
          dealer.state
        } ${dealer.zipCode}`,
        dealerLink: dealer.webAddress,
      },
    };
  };

  const ctaKeys = Object.keys(CTAs);

  const getDealerBingDirection = dealer
    ? `http://bing.com/maps/default.aspx?rtp=~adr.${dealer.address?.replace(
        ' ',
        '%20'
      )}%20${dealer.city?.replace(' ', '%20')},%20${dealer.state?.replace(
        ' ',
        '%20'
      )}%20${dealer.zipCode?.replace(' ', '%20')}`
    : '';
  return (
    <Card
      className={`${classes.root} ${
        isDealerDetail ? classes.noBorder : ''
      } dealer-card`}
    >
      <Grid container direction="row" className={classes.dealerHeader}>
        <div tabIndex={-1} className={classes.dealerIndex}>
          {isHondaThemed && (
            <Icon
              color={iconMainColor}
              type={Icons.pathPin}
              className={classes.location_pin}
            />
          )}
          <TextRender
            text={`${index}`}
            className={`${isHondaThemed ? 'atm-label-2' : 'acr-caption-10'} ${
              classes.index_text
            }`}
          />
        </div>
        <LinkButton
          onClick={() => {
            handleDetailsClick(dealer);
          }}
          classNames={{
            container: `${classes.dealerTitle}`,
          }}
          ariaLabel={`Go to ${dealer.name} information`}
        >
          <h3
            className={`${classes.dealerTitle} ${
              isHondaThemed ? 'atm-secondary-title-5' : 'acr-secondary-title-5'
            }`}
          >
            {dealer.name}
          </h3>
        </LinkButton>
      </Grid>
      <LinkButton
        onClick={() => handlePreferredDealerId()}
        classNames={{
          container: `${classes.preferredButton} ${
            currentPreferredDealer === dealer.dealerNumber && 'is-preferred'
          } preferred-button`,
          font: classes.preferredButtonLabel,
        }}
        ariaLabel={`Select ${dealer.name} as preferred dealer`}
      >
        <Icon
          color={light_grey_2}
          type={
            currentPreferredDealer === dealer.dealerNumber
              ? Icons.preferredDealerSelected
              : Icons.preferredDealer
          }
        />
      </LinkButton>
      <CardContent className={classes.dealerContent}>
        <Grid component="div" container direction="row">
          <Grid item xs={8} md={7}>
            <Grid container direction="row">
              <LinkButton
                variant={LinkButtonType.simple}
                target="_blank"
                to={getDealerBingDirection}
                classNames={{ container: classes.directionLink }}
                isExternal={true}
                onClick={() => {
                  handleDealerDirectionClick();
                }}
                ariaLabel={`Go to the ${dealer.name} direction`}
              >
                <Grid item xs={1} className={classes.directionIcon}>
                  <Icon
                    type={
                      isHondaThemed ? Icons.filledDirections : Icons.directions
                    }
                    color={iconMainColor}
                  />
                </Grid>
                <Grid
                  item
                  xs={10}
                  className={`${classes.dealerDirections} ${
                    isHondaThemed ? 'atm-label-1' : 'acr-paragraph-5'
                  } `}
                >
                  {dealer.drivingDistanceMiles && (
                    <TextRender
                      className={`${classes.dealerDistanceMiles}`}
                      tagName="p"
                      text={`${dealer.drivingDistanceMiles} mi`}
                    />
                  )}
                  <TextRender tagName="p" text={dealer.address as string} />{' '}
                  <TextRender
                    tagName="p"
                    text={`${dealer.city}, ${dealer.state} ${dealer.zipCode}`}
                  />
                </Grid>
              </LinkButton>
            </Grid>
          </Grid>
          <Grid item xs={4} md={5} className={classes.telephone}>
            <LinkButton
              variant={LinkButtonType.simple}
              classNames={{ container: classes.telephoneLink }}
              to={`tel:${dealer.phone}`}
              ariaLabel={`Go to the ${dealer.name} telephone`}
              isExternal={true}
              onClick={() => {
                handleDealerPhoneClick();
              }}
            >
              <Icon
                type={isHondaThemed ? Icons.filledTelephone : Icons.telephone}
                color={iconMainColor}
              />
              <p
                className={`${
                  isHondaThemed ? 'atm-label-1' : 'acr-paragraph-5'
                } ${classes.telephoneText}`}
              >
                <TextRender text={dealer.phone as string} />
              </p>
            </LinkButton>
          </Grid>
        </Grid>
      </CardContent>
      <CardActions className={classes.dealerCTAs}>
        {useDealerCtas ? (
          ctaKeys?.map((ctaKey: string) => {
            const button: IDealerCTA = CTAs[ctaKey];
            const url = getCtaURL(button, dealer);
            const ctaType = LinkButtonType[getButtonType(button.type)];
            const isTextButton = ctaType === LinkButtonType.text;
            if (url) {
              return (
                <LinkButton
                  dataAutomation={button.label}
                  key={button.label}
                  to={url}
                  variant={isHondaThemed ? LinkButtonType.filled : ctaType}
                  rounded={isHondaThemed}
                  isExternal={true}
                  target={button.target}
                  trackEventData={getTrackEventData(button.label, ctaType)}
                  classNames={{
                    container: `dealer-cta ${
                      isTextButton
                        ? classes.dealerTextButtonContainer
                        : classes.dealerCTAContainer
                    }`,
                    ...(theme.themeName === 'honda' && {
                      font: 'atm-caption-7',
                    }),
                  }}
                >
                  <span className={isTextButton ? classes.dealerCTAText : ''}>
                    <TextRender text={button.label} />
                    {isTextButton && (
                      <Icon
                        type={Icons.caretRight}
                        className={classes.dealerCTASvg}
                        color={acura_red}
                      />
                    )}
                  </span>
                </LinkButton>
              );
            } else {
              return null;
            }
          })
        ) : (
          <LinkButton
            dataAutomation={labels.selectADealer}
            classNames={{
              link: classes.dealerCTAButton,
              container: `select-a-dealer-cta ${classes.dealerCTAContainer}`,
              ...(theme.themeName === 'honda' && { font: 'atm-caption-7' }),
            }}
            onClick={selectDealer}
            variant={
              isHondaThemed ? LinkButtonType.filled : LinkButtonType.redOutlined
            }
            rounded={isHondaThemed}
            trackEventData={{
              label: `results:${labels.selectADealer}`,
              category: {
                includePageName: true,
                text: 'results',
              },
              type: 'cta',
              dealer: {
                dealerId: dealer.dealerNumber,
                dealerZipcode: dealer.zipCode,
                dealerName: dealer.name,
                dealerAddress: `${dealer.address as string}, ${dealer.city}, ${
                  dealer.state
                } ${dealer.zipCode}`,
                dealerLink: dealer.webAddress,
              },
            }}
          >
            <TextRender text={labels.selectADealer} />
          </LinkButton>
        )}
      </CardActions>
    </Card>
  );
}
